import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { PageLayout, Redirect } from "~/routes";
import {
  EmailPreferenceList,
  useSetEmailPreferenceMutation,
} from "~/operations";
import { FeatureFlags } from "~/login/feature-flags";
import { useFeatureFlags } from "~/providers/FeatureFlagsProvider";
import { Invitations } from "~/components/invitation";
import { Root } from "~/routes/authenticated-app/root";
import { SpaceRoutes } from "~/routes/space/space-routes";
import { CreateOrganizationPage } from "~/pages/organization/CreateOrganizationPage";
import { UserSettingsPage } from "~/pages/user-settings/UserSettingsPage";
import { PrintPage } from "~/pages/print/PrintPage";
import { NotFoundPage } from "~/pages/not-found/NotFoundPage";
import { OrganizationPages } from "~/pages/organization/OrganizationPages";
import { AwsWaiter } from "~/pages/space/home-dashboard/AwsGuide/AwsGuide";
import { RegionDashboardPage } from "~/pages/organization/DashboardRegionPage";
import { PlatformLayout } from "~/routes/platform/platform-layout";
import { PlatformPoliciesRoutes } from "~/routes/platform/policies/platformPolicies-routes";
import { StatusTagsExample } from "../../../docs/status-tags-example";

const IconsDiscovery = lazy(() => import("~/pages/icons"));

export default function AuthenticatedAppRoutes() {
  const { isFeatureEnabled } = useFeatureFlags();

  const [setEmailPreference] = useSetEmailPreferenceMutation();

  // This portion is explicitly for initial signup.  If a user
  // checks the box to opt in to emails, we will set their email preferences
  // here.
  let emailOptIn = localStorage.getItem("mondoo_emailOptIn");
  if (emailOptIn === "true") {
    setEmailPreference({
      variables: {
        input: [
          { listID: EmailPreferenceList.NewsletterEvents, subscribe: true },
          { listID: EmailPreferenceList.NewsletterGeneral, subscribe: true },
          { listID: EmailPreferenceList.NewsletterProduct, subscribe: true },
        ],
      },
    });
    localStorage.removeItem("mondoo_emailOptIn");
  }

  return (
    <Suspense>
      <Routes>
        <Route element={<Root />}>
          <Route element={<PageLayout />}>
            <Route element={<PlatformLayout />}>
              <Route index element={<RegionDashboardPage />} />
              <Route path="dashboard" element={<RegionDashboardPage />} />
              <Route
                path="organizations/new"
                element={<CreateOrganizationPage />}
              />
              {isFeatureEnabled("Platform Policy Management") && (
                <Route path="policies/*" element={<PlatformPoliciesRoutes />} />
              )}
            </Route>
            <Route path="flags" element={<FeatureFlags />} />
            {isFeatureEnabled("Developer Tools") && (
              <>
                <Route path="icons" element={<IconsDiscovery />} />
                <Route path="status-tags" element={<StatusTagsExample />} />
              </>
            )}
            <Route path="invitations" element={<Invitations />} />
            <Route path="settings/*" element={<UserSettingsPage />} />
          </Route>
          <Route path="space/*" element={<SpaceRoutes />} />
          <Route path="organization/*" element={<OrganizationPages />} />
        </Route>
        <Route path="print/*" element={<PrintPage />} />
        {/* AWS Campaign */}
        <Route path="aws-guide">
          <Route index element={<AwsWaiter />} />
        </Route>
        {/* Deprecated Routes */}
        <Route path="spaces" element={<Navigate replace to="/dashboard" />} />
        <Route
          path="library/*"
          element={<Redirect from="library" to="vulns" />}
        />
        <Route
          path="space/library/*"
          element={<Redirect from="space/library" to="space/vulns" />}
        />

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}
